import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CUIDARI_ACCESS_GATE_COPY } from '@francisquiniai/cuidari-core/auth'
import { useWaitlistGate } from '@/modules/waitlist/useWaitlistGate'
import {
  submitCredentials,
  submitMfa,
  submitReset,
  type LoginActionDeps,
  type LoginStage,
} from './loginActions'

export function useLoginFlow() {
  const { t } = useTranslation('auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [mfaUserId, setMfaUserId] = useState<string | null>(null)
  const [mfaDeadline, setMfaDeadline] = useState<number | null>(null)
  const [secondsLeft, setSecondsLeft] = useState<number>(0)
  const [stage, setStage] = useState<LoginStage>('credentials')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const gate = useWaitlistGate(CUIDARI_ACCESS_GATE_COPY.productSlug)

  useEffect(() => {
    if (stage !== 'mfa' || mfaDeadline === null) return
    const tick = () => {
      const left = Math.max(0, Math.ceil((mfaDeadline - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left <= 0 && tickRef.current) {
        clearInterval(tickRef.current)
        tickRef.current = null
      }
    }
    tick()
    tickRef.current = setInterval(tick, 1000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
    }
  }, [stage, mfaDeadline])

  const deps: LoginActionDeps = {
    t,
    gate,
    email,
    password,
    mfaCode,
    challengeToken,
    mfaUserId,
    setBusy,
    setError,
    setNotice,
    setChallengeToken,
    setMfaUserId,
    setMfaDeadline,
    setMfaCode,
    setStage,
  }
  const onCredentials = (e: FormEvent) => submitCredentials(deps, e)
  const onMfa = (e: FormEvent) => submitMfa(deps, e)
  const onReset = (e: FormEvent) => submitReset(deps, e)

  function startReset() {
    setStage('reset')
    setError(null)
  }

  function leaveMfa() {
    setStage('credentials')
    setMfaCode('')
    setChallengeToken(null)
    setMfaUserId(null)
    setMfaDeadline(null)
  }

  function leaveReset() {
    setStage('credentials')
    setNotice(null)
  }

  return {
    t,
    email,
    setEmail,
    password,
    setPassword,
    mfaCode,
    setMfaCode,
    secondsLeft,
    stage,
    busy,
    error,
    notice,
    onCredentials,
    onMfa,
    onReset,
    startReset,
    leaveMfa,
    leaveReset,
  }
}

export type LoginFlow = ReturnType<typeof useLoginFlow>
